import * as yup from 'yup';
import { IPublicSupportUser } from 'domain/entities/SupportUser/SupportUser';

export interface IBuyer {
    id: number;
    name: string;
    company_code: string;
    area: string[];
    target_company_industries: string[];
    target_company_industry_details: string | null;
    target_company_purchase_amount_lower: string | null;
    target_company_purchase_amount_upper: string | null;
    target_company_revenue_scale_lower: string | null;
    target_company_revenue_scale_upper: string | null;
    target_company_profit_level_lower: string | null;
    target_company_profit_level_upper: string | null;
    target_company_employee_size_lower: string | null;
    target_company_employee_size_upper: string | null;
    target_company_net_assets_level_lower: string | null;
    target_company_net_assets_level_upper: string | null;
    has_acquisition_experience: string | null;
    other_buying_points: string | null;
    number_of_employees: string;
    revenue_scale: string;
    icon_image: string | null;
    business_description: string;
    question: string;
    hp_url: string | null;
    label: string | null;
    fee_structure: boolean;
    other_buyers: boolean;
    contract_status: boolean;
    process_flow: boolean;
    business_synergy: boolean;
    support_system: boolean;
    fee_structure_txt: string | null;
    other_buyers_txt: string | null;
    contract_status_txt: string | null;
    process_flow_txt: string | null;
    business_synergy_txt: string | null;
    support_system_txt: string | null;
    unpublished: boolean;
    subsidy_info_requested?: boolean;
    is_sole_proprietor: boolean;
    created_at: string;
    supporter_user?: IPublicSupportUser | null;
}

export interface IPublicBuyer {
    id: number;
    area: string[];
    area_name: string[];
    target_company_industries: string[];
    target_company_net_assets_level: string | null;
    target_company_employee_size: string | null;
    target_company_industry_details: string | null;
    target_company_revenue_scale: string | null;
    target_company_profit_level: string | null;
    target_company_purchase_amount: string | null;
    has_acquisition_experience: string | null;
    other_buying_points: string | null;
    number_of_employees: string;
    revenue_scale: string;
    business_description: string;
    question: string;
    fee_structure: boolean;
    other_buyers: boolean;
    contract_status: boolean;
    process_flow: boolean;
    business_synergy: boolean;
    support_system: boolean;
    fee_structure_txt: string | null;
    other_buyers_txt: string | null;
    contract_status_txt: string | null;
    process_flow_txt: string | null;
    business_synergy_txt: string | null;
    support_system_txt: string | null;
    label: string | null;
    match_status?: string | null;
    is_sole_proprietor: boolean;
    created_at: string;
    updated_at: string;
}

export interface IBuyerDetailAfterMatch {
    id: number;
    name: string;
    company_code: string | null;
    area: string[];
    target_company_industries: string[];
    target_company_industry_details: string | null;
    target_company_net_assets_level: string | null;
    target_company_employee_size: string | null;
    target_company_revenue_scale: string | null;
    target_company_profit_level: string | null;
    target_company_purchase_amount: string | null;
    other_buying_points: string | null;
    number_of_employees: string;
    revenue_scale: string;
    icon_image: string | null;
    business_description: string;
    question: string;
    hp_url: string | null;
    label: string | null;
    fee_structure: boolean;
    other_buyers: boolean;
    contract_status: boolean;
    process_flow: boolean;
    business_synergy: boolean;
    support_system: boolean;
    fee_structure_txt: string | null;
    other_buyers_txt: string | null;
    contract_status_txt: string | null;
    process_flow_txt: string | null;
    business_synergy_txt: string | null;
    support_system_txt: string | null;
    unpublished: boolean;
    subsidy_info_requested?: boolean;
    is_sole_proprietor: boolean;
    created_at: string;
}

export interface IAreaOption {
    name: string;
    children: Array<{
        code: string;
        name: string;
    }>;
}

export const publicBuyerSchema = yup.object({
    id: yup.number().required(),
    company_code: yup.string().nullable().defined(),
    area: yup.array(yup.string().required()).required(),
    area_name: yup.array(yup.string().required()).required(),
    target_company_industry_details: yup.string().nullable().defined(),
    target_company_industries: yup.array(yup.string().required()).required(),
    target_company_employee_size: yup.string().nullable().defined(),
    target_company_net_assets_level: yup.string().nullable().defined(),
    target_company_revenue_scale: yup.string().nullable().defined(),
    target_company_profit_level: yup.string().nullable().defined(),
    target_company_purchase_amount: yup.string().nullable().defined(),
    has_acquisition_experience: yup.string().nullable().defined(),
    other_buying_points: yup.string().nullable().defined(),
    number_of_employees: yup.string().required(),
    revenue_scale: yup.string().required(),
    question: yup.string().defined(),
    business_description: yup.string().required(),
    fee_structure: yup.boolean().required(),
    other_buyers: yup.boolean().required(),
    contract_status: yup.boolean().required(),
    process_flow: yup.boolean().required(),
    business_synergy: yup.boolean().required(),
    support_system: yup.boolean().required(),
    fee_structure_txt: yup.string().defined(),
    other_buyers_txt: yup.string().defined(),
    contract_status_txt: yup.string().defined(),
    process_flow_txt: yup.string().defined(),
    business_synergy_txt: yup.string().defined(),
    support_system_txt: yup.string().defined(),
    is_sole_proprietor: yup.boolean().required(),
    label: yup.string().defined(),
    created_at: yup.string().required(),
    updated_at: yup.string().required(),
});

export const buyerSchema = publicBuyerSchema.concat(
    yup.object({
        target_company_industry_details: yup.string().nullable().defined(),
        target_company_purchase_amount_lower: yup.string().nullable().defined(),
        target_company_purchase_amount_upper: yup.string().nullable().defined(),
        target_company_employee_size_lower: yup.string().nullable().defined(),
        target_company_employee_size_upper: yup.string().nullable().defined(),
        target_company_net_assets_level_lower: yup.string().nullable().defined(),
        target_company_net_assets_level_upper: yup.string().nullable().defined(),
        target_company_revenue_scale_lower: yup.string().nullable().defined(),
        target_company_revenue_scale_upper: yup.string().nullable().defined(),
        target_company_profit_level_lower: yup.string().nullable().defined(),
        target_company_profit_level_upper: yup.string().nullable().defined(),
        target_company_revenue_scale: yup.string().nullable().defined(),
        target_company_profit_level: yup.string().nullable().defined(),
        target_company_purchase_amount: yup.string().nullable().defined(),
        other_buying_points: yup.string().nullable().defined(),
        name: yup.string().required(),
        icon_image: yup.string().nullable().defined(),
        company_code: yup.string().nullable().defined(),
        hp_url: yup.string().nullable().defined(),
        unpublished: yup.boolean().required(),
    }),
);

export const buyerDetailAfterMatchSchema = publicBuyerSchema.concat(
    yup.object({
        unpublished: yup.boolean().required(),
        name: yup.string().required(),
        icon_image: yup.string().nullable().defined(),
        hp_url: yup.string().nullable().defined(),
    }),
);
